// File: src/pages/German.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import config from "../config/config";

// shadcn/ui – identisch zum CV-Pro-Screen
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";

import {
  CheckCircle2, Languages, Target, GraduationCap, Timer, ShieldCheck, AlertTriangle, RefreshCw
} from "lucide-react";
import { getStoredIdentity } from "../utils/authSession";

/** ————————————————————————————————————————  Hilfs-UI (CV-Style) ———————————————————————————————————————— */
const SectionCard = ({ title, icon: Icon, right, children, className }) => (
  <Card className={`bg-slate-900/60 border-slate-800 shadow-xl ${className || ""}`}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-lg md:text-xl text-slate-100 flex items-center gap-2">
        {Icon ? <Icon size={20} className="text-emerald-400" /> : null}
        {title}
      </CardTitle>
      <div className="flex items-center gap-2">{right}</div>
    </CardHeader>
    <CardContent className="text-slate-200">{children}</CardContent>
  </Card>
);

const Tag = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-700 px-2 py-0.5 text-xs text-slate-300">
    {children}
  </span>
);

const letter = (i) => String.fromCharCode(65 + i); // A,B,C,D…

/** CEFR-Badge – Serverwert bevorzugt, sonst heuristische Einordnung */
function CEFRBadge({ cefr, score0_100 }) {
  const v = (score0_100 ?? 0);
  const fallback = v <= 20 ? "A1" : v <= 35 ? "A2" : v <= 55 ? "B1" : v <= 75 ? "B2" : v <= 90 ? "C1" : "C2";
  const band = (cefr || fallback);
  return (
    <Badge variant="outline" className="border-emerald-600 text-emerald-300">{band}</Badge>
  );
}

/** Einheitlicher Item-Renderer (MC/MS/GAP/WRITE) */
const ItemView = ({ item, onAnswer }) => {
  const [val, setVal] = useState(null);
  const [multi, setMulti] = useState([]);

  const type = item.type || item.qtype || "mc";
  const options = Array.isArray(item.options) ? item.options : [];

  const submit = () => {
    if (type === "mc" || type === "read_mc" || type === "listen_mc") {
      if (val == null) return;
      onAnswer({ index: Number(val) });
    } else if (type === "ms") {
      onAnswer({ indices: multi.map(Number) });
    } else if (type === "gap") {
      onAnswer({ answers: [String(val || "").trim()] });
    } else if (type === "write") {
      onAnswer({ text: String(val || "").trim() });
    } else if (type === "short") {
      // Legacy „Kurzantwort“ → Backend bewerten lassen
      onAnswer({ text: String(val || "").trim() });
    } else {
      onAnswer({ raw: val });
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-base leading-relaxed whitespace-pre-wrap">{item.stem_text || item.prompt}</div>

      {(type === "mc" || type === "read_mc" || type === "listen_mc") && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {options.map((opt, i) => (
            <label key={i} className="flex items-center gap-2 bg-slate-900 rounded-lg px-3 py-2 border border-slate-700 cursor-pointer">
              <input type="radio" name={`mc-${item.id}`} checked={Number(val)===i} onChange={() => setVal(i)} />
              <span className="text-slate-200">{letter(i)}. {opt}</span>
            </label>
          ))}
        </div>
      )}

      {type === "ms" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {options.map((opt, i) => {
            const checked = multi.includes(i);
            return (
              <label key={i} className="flex items-center gap-2 bg-slate-900 rounded-lg px-3 py-2 border border-slate-700 cursor-pointer">
                <input type="checkbox" checked={checked} onChange={(e) => {
                  setMulti((prev) => e.target.checked ? [...prev, i] : prev.filter(x => x !== i));
                }} />
                <span className="text-slate-200">{letter(i)}. {opt}</span>
              </label>
            );
          })}
        </div>
      )}

      {(type === "gap" || type === "short") && (
        <Input placeholder="Antwort eingeben…" value={val || ""} onChange={(e)=>setVal(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-100" />
      )}

      {type === "write" && (
        <Textarea rows={6} placeholder="Kurztext eingeben…" value={val || ""} onChange={(e)=>setVal(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-100" />
      )}

      <div className="flex justify-end">
        <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-500">Antwort senden</Button>
      </div>
    </div>
  );
};

/** ————————————————————————————————————————  Daten/Adapter (neu ↔ legacy) ———————————————————————————————————————— */
const API = {
  base: config.API_BASE_URL || "",
  token: () => "",
  userid: () => getStoredIdentity().userid || "ANON",
};

async function safeGet(url, cfg={}) {
  try { return await axios.get(url, cfg); }
  catch (e) { return { error: e }; }
}
async function safePost(url, body={}, cfg={}) {
  try { return await axios.post(url, body, cfg); }
  catch (e) { return { error: e }; }
}

/** Frische Engine (CAT/Session) – gemäß Konzept */ // :contentReference[oaicite:3]{index=3}
const NewAPI = {
  async startCAT() {
    return await safePost(`${API.base}/api/german/cat/start`, { userId: API.userid() });
  },
  async nextCAT(session_id) {
    return await safeGet(`${API.base}/api/german/cat/next`, { params: { session_id }});
  },
  async respCAT(session_id, item_id, response) {
    return await safePost(`${API.base}/api/german/cat/response`, { session_id, item_id, response });
  },
  async finishCAT(session_id) {
    return await safePost(`${API.base}/api/german/cat/finish`, { session_id });
  },

  async startPractice(level, count) {
    return await safePost(`${API.base}/api/german/session/start`, {
      userId: API.userid(), mode: "practice", target: { level, skills: [], count }
    });
  },
  async respPractice(session_id, item_id, response) {
    return await safePost(`${API.base}/api/german/session/${session_id}/response`, { item_id, response });
  },
  async finishPractice(session_id) {
    return await safePost(`${API.base}/api/german/session/${session_id}/finish`, {});
  },

  async summary() { return await safeGet(`${API.base}/api/german/summary`, { params: { userid: API.userid() }}); },
  async employer() { return await safeGet(`${API.base}/api/german/employer-score`, { params: { userid: API.userid(), job: "default" }}); },
};

/** Legacy-API (Fragen + Submit) – falls Neu-API (noch) fehlt */ // :contentReference[oaicite:4]{index=4}
const LegacyAPI = {
  async questions(limit=12) {
    // Erst /api/german/questions … wenn 404 → /api/deutschtest?limit=
    const a = await safeGet(`${API.base}/api/german/questions`, { params: { limit }});
    if (!a.error && Array.isArray(a.data)) return { data: a.data.map(mapLegacyQ) };
    const b = await safeGet(`${API.base}/api/deutschtest`, { params: { limit }});
    if (!b.error && Array.isArray(b.data)) return { data: b.data.map(mapLegacyQ) };
    return { error: a.error || b.error || new Error("Keine Legacy-Route gefunden") };
  },
  async submit(answers, duration_ms) {
    const payload = { userid: API.userid(), answers, duration_ms, meta: { ua: navigator.userAgent } };
    const p1 = await safePost(`${API.base}/api/german/submit`, payload);
    if (!p1.error) return p1;
    const p2 = await safePost(`${API.base}/api/deutschtest/submit`, payload);
    return p2;
  },
  async results() {
    const r1 = await safeGet(`${API.base}/api/german/results`, { params: { userid: API.userid() }});
    if (!r1.error) return r1;
    const r2 = await safeGet(`${API.base}/api/deutschtest/results`, { params: { userid: API.userid() }});
    return r2;
  },
};

function mapLegacyQ(q) {
  // Vereinheitliche Form auf {id, type, stem_text, options}
  return {
    id: q.id,
    type: q.qtype || "mc",
    stem_text: q.prompt || q.stem_text,
    options: Array.isArray(q.options) ? q.options : [],
    module: q.module,
    time_limit: q.timeLimitSec || q.time_limit || null
  };
}

/** ————————————————————————————————————————  Seite ———————————————————————————————————————— */
export default function German() {
  const [tab, setTab] = useState("placement"); // placement | coach | history
  const [busy, setBusy] = useState(false);

  // ——— History / Employer
  const [summary, setSummary] = useState(null);
  const [employer, setEmployer] = useState(null);
  const loadSummary = async () => { const r = await NewAPI.summary(); if (!r.error) setSummary(r.data); };
  const loadEmployer = async () => { const r = await NewAPI.employer(); if (!r.error) setEmployer(r.data); };
  useEffect(() => { loadSummary(); loadEmployer(); }, []);

  /** ===== Einstufung (CAT → Fallback Legacy) ===== */
  const [catSid, setCatSid] = useState(null);
  const [catItem, setCatItem] = useState(null);
  const [catCount, setCatCount] = useState(0);
  const [catTheta, setCatTheta] = useState(null);
  const [catSE, setCatSE] = useState(null);
  const [catCEFR, setCatCEFR] = useState(null);
  const [legacyPack, setLegacyPack] = useState(null); // fallback {items:[], idx:0, answers:[]}
  const CAT_MAX = 12;

  const startPlacement = async () => {
    setBusy(true);
    // 1) Versuch: neue CAT-API
    const s = await NewAPI.startCAT();
    if (!s.error && s.data?.session_id) {
      setCatSid(s.data.session_id);
      setCatCount(0); setCatTheta(null); setCatSE(null); setCatCEFR(null);
      const nx = await NewAPI.nextCAT(s.data.session_id);
      if (!nx.error && nx.data?.item) { setCatItem(nx.data.item); setLegacyPack(null); setBusy(false); return; }
    }
    // 2) Fallback: Legacy-„Mini-Prüfung“ (12 Fragen)
    const q = await LegacyAPI.questions(CAT_MAX);
    if (!q.error) {
      setLegacyPack({ items: q.data || [], idx: 0, answers: [] });
      setCatSid(null); setCatItem(null);
    } else {
      alert("Weder CAT noch Legacy-Fragen erreichbar.");
    }
    setBusy(false);
  };

  const answerPlacement = async (payload) => {
    if (catSid && catItem) {
      setBusy(true);
      const r = await NewAPI.respCAT(catSid, catItem.id, payload);
      if (!r.error) {
        setCatTheta(r.data?.theta ?? catTheta);
        setCatSE(r.data?.se ?? catSE);
        setCatCEFR(r.data?.provisional_cefr ?? catCEFR);
        setCatCount((c)=>c+1);
        if ((r.data?.se && r.data.se <= 0.35) || (catCount+1)>=CAT_MAX || r.data?.done) {
          await finishPlacement();
        } else {
          const nx = await NewAPI.nextCAT(catSid);
          if (!nx.error && nx.data?.item) setCatItem(nx.data.item);
        }
      }
      setBusy(false);
      return;
    }
    // Legacy Pfad
    if (legacyPack) {
      const nextAnswers = [...legacyPack.answers, payload];
      const nextIdx = legacyPack.idx + 1;
      if (nextIdx >= legacyPack.items.length) {
        await finishPlacement(nextAnswers);
      } else {
        setLegacyPack({ ...legacyPack, idx: nextIdx, answers: nextAnswers });
      }
    }
  };

  const finishPlacement = async (legacyAnswers=null) => {
    setBusy(true);
    // Neu
    if (catSid) {
      const f = await NewAPI.finishCAT(catSid);
      if (!f.error) {
        // Refresh Overview
        await loadSummary(); await loadEmployer();
      }
      setCatSid(null); setCatItem(null);
      setBusy(false);
      return;
    }
    // Legacy: sende alle Antworten
    if (legacyPack && Array.isArray(legacyAnswers)) {
      const answers = legacyPack.items.map((it, i) => {
        const a = legacyAnswers[i] || {};
        if (it.type === "mc") return { questionId: it.id, userAnswer: letter(a.index ?? -1) };
        if (it.type === "short" || it.type === "gap" || it.type === "write") return { questionId: it.id, userAnswer: a.text || (a.answers?.[0] || "") };
        // Default
        return { questionId: it.id, userAnswer: a.text || "" };
      });
      await LegacyAPI.submit(answers, 0);
      await LegacyAPI.results(); // nur warm-up
      setLegacyPack(null);
      await loadSummary(); await loadEmployer();
    }
    setBusy(false);
  };

  /** ===== Coach (Practice) – Neu → Legacy ===== */
  const [pSid, setPSid] = useState(null);
  const [pItems, setPItems] = useState([]);
  const [pIdx, setPIdx] = useState(0);
  const [pFeedback, setPFeedback] = useState(null);
  const [pLevel, setPLevel] = useState("B1");
  const [pCount, setPCount] = useState(10);
  const [pLegacy, setPLegacy] = useState(null); // {items, idx, answers}

  const startPractice = async () => {
    setBusy(true);
    const r = await NewAPI.startPractice(pLevel, pCount);
    if (!r.error && r.data?.session_id) {
      setPSid(r.data.session_id);
      setPItems(Array.isArray(r.data.items) ? r.data.items : []);
      setPIdx(0); setPFeedback(null); setPLegacy(null);
      setBusy(false); return;
    }
    // Legacy-Fallback
    const q = await LegacyAPI.questions(pCount);
    if (!q.error) {
      setPLegacy({ items: q.data, idx: 0, answers: [] }); setPItems([]); setPSid(null);
    } else {
      alert("Coach: Keine passenden Endpunkte erreichbar.");
    }
    setBusy(false);
  };

  const answerPractice = async (payload) => {
    setBusy(true);
    if (pSid && pItems[pIdx]) {
      const it = pItems[pIdx];
      const r = await NewAPI.respPractice(pSid, it.id, payload);
      setPFeedback(r.error ? null : (r.data?.judged || null));
      setTimeout(() => { setPFeedback(null); setPIdx((i)=>i+1); setBusy(false); }, 350);
      return;
    }
    if (pLegacy) {
      const next = [...pLegacy.answers, payload];
      const nextIdx = pLegacy.idx + 1;
      if (nextIdx >= pLegacy.items.length) {
        // Sende Legacy-Paket
        const answers = pLegacy.items.map((it, i) => {
          const a = next[i] || {};
          if (it.type === "mc") return { questionId: it.id, userAnswer: letter(a.index ?? -1) };
          return { questionId: it.id, userAnswer: a.text || (a.answers?.[0] || "") };
        });
        await LegacyAPI.submit(answers, 0);
        await LegacyAPI.results();
        setPLegacy(null);
        await loadSummary(); await loadEmployer();
      } else {
        setPLegacy({ ...pLegacy, idx: nextIdx, answers: next });
      }
      setBusy(false);
    }
  };

  /** ————————————————————————————————————————  Render  ———————————————————————————————————————— */
  return (
    <motion.div className="min-h-screen px-4 md:px-6 py-8 md:py-10 text-white max-w-5xl mx-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45 }}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">🇩🇪 Deutsch · Coach & Einstufung</h1>
          <p className="text-slate-300 mt-1 text-base">Einheitliches UI wie beim CV‑Pro, mit sofort lauffähigen Fallbacks.</p>
        </div>
        <div className="min-w-[220px]">
          <div className="flex items-center justify-between text-slate-300 text-sm mb-1">
            <span>Letztes Ergebnis</span>
            <span>{summary?.last_result?.norm_score_0_100 ?? "–"}%</span>
          </div>
          <Progress value={Number(summary?.last_result?.norm_score_0_100 || 0)} className="h-2 bg-slate-800" />
        </div>
      </div>

      <Tabs defaultValue="placement" className="w-full" value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="placement">Einstufung</TabsTrigger>
          <TabsTrigger value="coach">Coach (Üben)</TabsTrigger>
          <TabsTrigger value="history">Verlauf & Arbeitgeber</TabsTrigger>
        </TabsList>

        {/* ===== Einstufung ===== */}
        <TabsContent value="placement" className="mt-6">
          <SectionCard
            title="Einstufungstest"
            icon={Target}
            right={<Button onClick={startPlacement} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500">{catSid || legacyPack ? "Neu starten" : "Starten"}</Button>}
          >
            {!catSid && !legacyPack && <p className="text-slate-300">Adaptiv (CAT) wenn verfügbar, sonst 12 Fragen im Legacy‑Modus. Ergebnisse landen in deinem Profil/Arbeitgeber‑Score.</p>}

            {/* CAT aktiv */}
            {catSid && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Tag>Session: {catSid}</Tag>
                  <Tag>θ {catTheta!=null ? catTheta.toFixed(2) : "—"}</Tag>
                  <Tag>SE {catSE!=null ? catSE.toFixed(2) : "—"}</Tag>
                  <Tag>vorläufig: <b>{catCEFR || "—"}</b></Tag>
                  <Tag>Fortschritt {catCount}/{CAT_MAX}</Tag>
                </div>
                {catItem ? (
                  <SectionCard title="Aufgabe" icon={GraduationCap}>
                    <ItemView item={catItem} onAnswer={answerPlacement} />
                  </SectionCard>
                ) : (
                  <div className="text-slate-300">Nächste Aufgabe wird geladen…</div>
                )}
                <div className="flex justify-end"><Button variant="outline" onClick={()=>finishPlacement()}><RefreshCw className="mr-1" size={16}/>Jetzt beenden</Button></div>
              </div>
            )}

            {/* Legacy aktiv */}
            {legacyPack && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Tag>Legacy‑Fragen</Tag>
                  <Tag>{legacyPack.idx + 1}/{legacyPack.items.length}</Tag>
                </div>
                <SectionCard title={`Aufgabe ${legacyPack.idx + 1}`} icon={GraduationCap}>
                  <ItemView item={legacyPack.items[legacyPack.idx]} onAnswer={answerPlacement} />
                </SectionCard>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* ===== Coach (Practice) ===== */}
        <TabsContent value="coach" className="mt-6">
          <SectionCard
            title="Coach (gezielte Übung)"
            icon={Languages}
            right={<Button onClick={startPractice} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500">Session starten</Button>}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label>Level</Label>
                <Select value={pLevel} onValueChange={setPLevel}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 mt-1">
                    <SelectValue placeholder="Level wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {["A1","A2","B1","B2","C1","C2"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Anzahl Aufgaben</Label>
                <Input type="number" min={5} max={30} value={pCount} onChange={(e)=>setPCount(Number(e.target.value || 10))} className="bg-slate-800 border-slate-700 text-slate-100 mt-1" />
              </div>
            </div>

            {/* Neue Engine */}
            {pSid && pItems.length>0 && pIdx < pItems.length && (
              <>
                <div className="flex items-center justify-between mb-2 text-sm text-slate-300">
                  <div className="flex items-center gap-2">
                    <Tag>Session {pSid}</Tag>
                    <Tag>Aufgabe {pIdx+1}/{pItems.length}</Tag>
                  </div>
                </div>
                <SectionCard title="Aufgabe" icon={GraduationCap}>
                  <ItemView item={pItems[pIdx]} onAnswer={answerPractice} />
                  {pFeedback && (
                    <div className="mt-3">
                      {pFeedback.isCorrect ? <Badge className="bg-emerald-700">✓ korrekt</Badge> : <Badge className="bg-slate-700">✗</Badge>}
                      {" "}
                      {typeof pFeedback.partial === "number" ? <Tag>Teilpunkte: {Math.round(pFeedback.partial*100)}%</Tag> : null}
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {/* Legacy */}
            {pLegacy && (
              <>
                <div className="flex items-center justify-between mb-2 text-sm text-slate-300">
                  <Tag>Legacy‑Übung</Tag>
                  <Tag>Aufgabe {pLegacy.idx+1}/{pLegacy.items.length}</Tag>
                </div>
                <SectionCard title="Aufgabe" icon={GraduationCap}>
                  <ItemView item={pLegacy.items[pLegacy.idx]} onAnswer={answerPractice} />
                </SectionCard>
              </>
            )}

            {!pSid && !pLegacy && (
              <Alert className="bg-slate-900/60 border-slate-800">
                <AlertTitle>Hinweis</AlertTitle>
                <AlertDescription className="text-slate-300">
                  Wenn die neue Session‑API nicht verfügbar ist, trainierst du automatisch mit Legacy‑Fragen.
                </AlertDescription>
              </Alert>
            )}
          </SectionCard>
        </TabsContent>

        {/* ===== Verlauf / Arbeitgeber ===== */}
        <TabsContent value="history" className="mt-6 space-y-6">
          <SectionCard title="Verlauf & Übersicht" icon={Timer}>
            {!summary ? <div className="text-slate-300">–</div> : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-slate-300">Letzter Score:</span>
                  <Badge variant="secondary" className="bg-slate-800 border-slate-700">
                    {summary.last_result?.norm_score_0_100 ?? "–"} / 100
                  </Badge>
                  <span className="ml-2 text-slate-300">CEFR:</span>
                  <CEFRBadge cefr={summary.cefr} score0_100={summary.last_result?.norm_score_0_100} />
                </div>
                <div className="text-xs text-slate-400">Start: {summary.last_result?.started_at || "–"} · Ende: {summary.last_result?.ended_at || "–"}</div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Arbeitgeber‑Score" icon={ShieldCheck}>
            {!employer ? <div className="text-slate-300">–</div> : (
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full border-8 border-emerald-600 grid place-items-center font-bold">
                  {Number(employer.score_0_100 || 0).toFixed(0)}
                </div>
                <div className="space-y-1">
                  <div>CEFR: <CEFRBadge cefr={employer.cefr} score0_100={employer.score_0_100} /></div>
                  <div className="text-sm text-slate-300">Zuletzt geprüft: {employer.last_test_iso || "–"}</div>
                </div>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
